import type { FairUseErrorCode } from "./fair-use-utils"
import type { MyUsage } from "@/api/fair-use"
import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "@/lib/query-keys"

import { extractPlanUpgradePayload, PLAN_UPGRADE_REQUIRED_CODE } from "./fair-use-plan-utils"
import { useFairUseStore } from "./fair-use-store"
import {
  extractFairUseError,
  extractFairUseMetadata,
  hasStaleRequestVerdict,
  mergeFairUseWsMetadata,
} from "./fair-use-utils"

/**
 * Refetch the authoritative snapshot when a live merge left the cached
 * `request` verdict behind.
 *
 * WS updates carry `{percent, status}` only, so a user who crosses the
 * configured CTA (or gets blocked) mid-session keeps the
 * `request.allowed:false` verdict from the session-start GET and loses the
 * "Request more" button exactly when it matters. Only GET /api/me/usage
 * recomputes `allowed`.
 *
 * Loop-safe by construction: the invalidation fires only when the merge
 * actually moved `percent`/`status` (an unchanged frame is a no-op) and only
 * while the cached verdict is still stale, so a settled snapshot — refreshed,
 * pending, or already allowed — triggers nothing.
 */
function refreshStaleRequestVerdict(
  queryClient: QueryClient,
  previous: Pick<MyUsage, "percent" | "status">,
  merged: MyUsage
): void {
  if (previous.percent === merged.percent && previous.status === merged.status) return
  if (!hasStaleRequestVerdict(merged)) return
  void queryClient.invalidateQueries({ queryKey: queryKeys.fairUseMyUsage })
}

/** True when a merge left every field the surfaces read exactly as it was. */
function isUnchangedSnapshot(current: MyUsage, merged: MyUsage): boolean {
  return (
    current.percent === merged.percent &&
    current.status === merged.status &&
    current.cost_sek === merged.cost_sek &&
    current.cap_sek === merged.cap_sek
  )
}

/**
 * Route the `fair_use: { percent, status, cost_sek, cap_sek }` metadata
 * appended to the final WS message of a completed answer into the TanStack
 * Query cache so the composer indicator updates live — no polling (spec
 * section 7). No-op when the payload carries no fair-use metadata (every
 * non-fair-use message).
 *
 * The money figures are merged alongside the movement so the popover's percent
 * and its spend line always describe the same instant; see
 * {@link mergeFairUseWsMetadata} for what happens when an older backend sends
 * the movement alone.
 */
export function routeFairUseMetadata(queryClient: QueryClient, payload: unknown): void {
  const metadata = extractFairUseMetadata(payload)
  if (!metadata) return
  const current = queryClient.getQueryData<MyUsage>(queryKeys.fairUseMyUsage)
  if (current) {
    const merged = mergeFairUseWsMetadata(current, metadata)
    // Skip the cache write (and the re-render it triggers) when the snapshot
    // already reflects this metadata — the common case for consecutive answers.
    if (isUnchangedSnapshot(current, merged)) return
    queryClient.setQueryData<MyUsage>(queryKeys.fairUseMyUsage, merged)
    refreshStaleRequestVerdict(queryClient, current, merged)
  } else {
    // Nothing cached yet (indicator not mounted or first answer of the
    // session) — the metadata alone is not a full snapshot, so just mark the
    // query stale and let the next mount fetch fresh data.
    void queryClient.invalidateQueries({ queryKey: queryKeys.fairUseMyUsage })
  }
}

/**
 * GET /api/me/usage returns `{enabled: false}` and nothing else while fair use
 * is off. That stub is not a mergeable snapshot: spreading a blocked-error
 * usage object onto it writes `enabled: true` without `request`, which mounts
 * the composer indicator and then throws on `usage.request.pending`.
 * `hasStaleRequestVerdict` treats a missing `request` as not-stale, so the
 * half-snapshot would never be refetched.
 */
function isUnmergeableUsageCache(current: MyUsage): boolean {
  return current.enabled === false || current.request == null
}

/**
 * Detect and route a structured fair-use WS error: refresh the usage cache
 * from the error's usage snapshot and auto-open the indicator popover, so the
 * "Request more" button is one click away from the refusal that just happened.
 * Returns the error's code so the caller can render the matching localized
 * notice instead of the raw backend message, or null — with no side effects —
 * for anything else.
 *
 * The two codes differ in what they may assume about the user:
 * - `fair_use_blocked` pins `status: "block"`. The turn was refused *because*
 *   the budget is spent, so the snapshot is blocked whatever a stale cached
 *   status says (and the indicator must show its lock).
 * - `fair_use_deep_unavailable` pins nothing. The user is near the cap but NOT
 *   out of budget — ordinary chat still works — so forcing `block` here would
 *   put a lock on the composer and "your budget is finished" copy in the
 *   popover for someone who can keep chatting. The payload's own status (or,
 *   failing that, the cached one) stands.
 *
 * A cached disabled/request-less stub is never merged. The error's usage is
 * a partial snapshot (no `request`), so overlaying it would mount the
 * indicator in a state the popover cannot render; invalidate and refetch the
 * authoritative GET instead.
 */
export function routeFairUseError(
  queryClient: QueryClient,
  errorMsg: unknown
): FairUseErrorCode | null {
  const error = extractFairUseError(errorMsg)
  if (!error) return null

  // A plan refusal is NOT a budget state (fair use round 2). The month's
  // figures are untouched and still correct, so nothing is merged into the
  // usage cache and no refetch is triggered — writing `{...current}` would
  // only churn the query and re-render every surface for no new information.
  // The payload is parked on the store so the in-chat card can name the
  // feature and the required plans, and the popover is opened so the user
  // lands on their plan and what it locks.
  if (error.code === PLAN_UPGRADE_REQUIRED_CODE) {
    useFairUseStore.getState().setPlanUpgrade(extractPlanUpgradePayload(errorMsg))
    useFairUseStore.getState().openPopover()
    return error.code
  }

  const current = queryClient.getQueryData<MyUsage>(queryKeys.fairUseMyUsage)
  if (current && !isUnmergeableUsageCache(current)) {
    const merged: MyUsage = { ...current, ...error.usage }
    if (error.code === "fair_use_blocked") merged.status = "block"
    queryClient.setQueryData<MyUsage>(queryKeys.fairUseMyUsage, merged)
    refreshStaleRequestVerdict(queryClient, current, merged)
  } else {
    void queryClient.invalidateQueries({ queryKey: queryKeys.fairUseMyUsage })
  }
  useFairUseStore.getState().openPopover()
  return error.code
}
